import { useState } from 'react';
import toast from 'react-hot-toast';
import { Field, Toggle } from '@components/atoms';
import { RwLoading } from '@components/RwLoading';
import { t } from '@services/i18n';
import { useApiQuery } from '@hooks/useApiQuery';
import { apiGet, apiPost } from '@services/api-client';
import type { MemberScreenProps } from './types';

type BoolPref = 'publicProfile' | 'onLeaderboard' | 'useAlias' | 'badgeEmails' | 'expiryReminders' | 'campaignUpdates';

interface Preferences {
  publicProfile: boolean;
  onLeaderboard: boolean;
  useAlias: boolean;
  alias: string;
  badgeEmails: boolean;
  expiryReminders: boolean;
  campaignUpdates: boolean;
  /** Read-only: an administrator has hidden this member from the public board. */
  leaderboardHidden?: boolean;
}

interface PreferencesResponse {
  preferences: Preferences;
}

// Privacy-safe empty default shape, used only as the standalone-harness fallback
// and resolver default. In Joomla the `preferences` endpoint always returns the
// member's consent row (or privacy-safe defaults when none exists), so this is
// never surfaced as data.
const emptyPrefs: Preferences = {
  publicProfile: false,
  onLeaderboard: false,
  useAlias: false,
  alias: '',
  badgeEmails: false,
  expiryReminders: false,
  campaignUpdates: false,
};

/**
 * Notifications & privacy (member) - visibility + notification toggles, the
 * GDPR export / removal controls, and the published point-expiration policy.
 * Toggles seed from live preferences (edits-overlay style); the Save action
 * persists them. The data-request buttons are stubbed to a toast.
 */
export function UserPrivacy(_props: MemberScreenProps) {
  const { data, isLoading } = useApiQuery<PreferencesResponse>(['rw-privacy'], 'preferences', { fallback: { preferences: emptyPrefs } });

  // Local edits overlay the loaded preferences; only touched keys live here.
  const [edits, setEdits] = useState<Partial<Preferences>>({});
  const [dataBusy, setDataBusy] = useState<string | null>(null);

  // Resolve a toggle value: local edit if touched, else loaded data, else privacy-safe false.
  const value = (k: BoolPref): boolean => (edits[k] as boolean | undefined) ?? data?.preferences?.[k] ?? false;
  const toggle = (k: BoolPref) => setEdits((e) => ({ ...e, [k]: !value(k) }));
  const aliasValue = (): string => (edits.alias ?? data?.preferences?.alias ?? '') as string;
  const setAlias = (v: string): void => setEdits((e) => ({ ...e, alias: v }));

  if (isLoading) return <RwLoading variant="page" />;

  const onSave = async () => {
    const currentValues: Preferences = {
      publicProfile: value('publicProfile'),
      onLeaderboard: value('onLeaderboard'),
      useAlias: value('useAlias'),
      alias: aliasValue().trim(),
      badgeEmails: value('badgeEmails'),
      expiryReminders: value('expiryReminders'),
      campaignUpdates: value('campaignUpdates'),
    };
    await apiPost('preferencesSave', { preferences: currentValues });
    toast.success(t('COM_REWARDIFY_PREFS_SAVED', 'Preferences saved'));
  };

  // GDPR export: download everything the API holds about the member as JSON.
  const downloadData = async (): Promise<void> => {
    if (dataBusy) return;
    setDataBusy('export');
    try {
      const payload = await apiGet<unknown>('export');
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'rewardify-my-data.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('COM_REWARDIFY_UPRIV_EXPORT_DONE', 'Your data has been downloaded'));
    } catch {
      toast.error(t('COM_REWARDIFY_UPRIV_EXPORT_FAILED', 'Could not export your data'));
    } finally {
      setDataBusy(null);
    }
  };

  // GDPR erasure: file a removal request an administrator reviews + actions.
  const requestRemoval = async (): Promise<void> => {
    if (dataBusy) return;
    if (!window.confirm(t('COM_REWARDIFY_UPRIV_REMOVAL_CONFIRM', 'Request permanent removal of your rewards data? An administrator will review and action this request.'))) {
      return;
    }
    setDataBusy('removal');
    try {
      const res = await apiPost<{ already?: boolean }>('requestRemoval');
      toast.success(
        res?.already
          ? t('COM_REWARDIFY_UPRIV_REMOVAL_ALREADY', 'You already have a removal request pending review.')
          : t('COM_REWARDIFY_UPRIV_REMOVAL_DONE', 'Removal requested. An administrator will review it.'),
      );
    } catch {
      toast.error(t('COM_REWARDIFY_UPRIV_REMOVAL_FAILED', 'Could not submit your request'));
    } finally {
      setDataBusy(null);
    }
  };

  return (
    <div className="rw-page rw-user">
      <header className="rw-page-h">
        <div>
          <div className="rw-eyebrow">{t('COM_REWARDIFY_UPRIV_EYEBROW', 'Preferences')}</div>
          <h1 className="rw-h1">{t('COM_REWARDIFY_UPRIV_TITLE', 'Notifications & privacy')}</h1>
          <p className="rw-lede">{t('COM_REWARDIFY_UPRIV_LEDE', "You control what's shared and how you're contacted. Change anything, any time.")}</p>
        </div>
      </header>

      <div className="rw-grid-2">
        <section className="rw-card">
          <h3 className="rw-card-t">{t('COM_REWARDIFY_UPRIV_VISIBILITY', 'Visibility')}</h3>
          <Field label={t('COM_REWARDIFY_UPRIV_PUBLIC_PROFILE', 'Public profile')} hint={t('COM_REWARDIFY_UPRIV_PUBLIC_PROFILE_HINT', 'Show your badges and level on your public profile.')}>
            <Toggle on={value('publicProfile')} onClick={() => toggle('publicProfile')} />
          </Field>
          <Field
            label={t('COM_REWARDIFY_UPRIV_APPEAR_LEADERBOARD', 'Appear on the leaderboard')}
            hint={data?.preferences?.leaderboardHidden
              ? t('COM_REWARDIFY_UPRIV_LEADERBOARD_HIDDEN', 'An administrator has hidden you from the public leaderboard. This setting takes effect again if they reverse that.')
              : undefined}
          >
            <Toggle on={value('onLeaderboard')} onClick={() => toggle('onLeaderboard')} />
          </Field>
          <Field label={t('COM_REWARDIFY_UPRIV_SHOW_ALIAS', 'Show an alias instead of my name')} hint={t('COM_REWARDIFY_UPRIV_SHOW_ALIAS_HINT', "Display 'm.linde' rather than your full name.")}>
            <Toggle on={value('useAlias')} onClick={() => toggle('useAlias')} />
          </Field>
          {value('useAlias') && (
            <Field label={t('COM_REWARDIFY_UPRIV_ALIAS_LABEL', 'Your alias')} hint={t('COM_REWARDIFY_UPRIV_ALIAS_HINT', 'Shown on the leaderboard and your public profile instead of your name. Leave blank to use your name.')}>
              <input
                className="rw-input"
                type="text"
                maxLength={40}
                value={aliasValue()}
                placeholder={t('COM_REWARDIFY_UPRIV_ALIAS_PH', 'e.g. m.linde')}
                onChange={(e) => setAlias(e.target.value)}
              />
            </Field>
          )}
        </section>

        <section className="rw-card">
          <h3 className="rw-card-t">{t('COM_REWARDIFY_UPRIV_NOTIFICATIONS', 'Notifications')}</h3>
          <Field label={t('COM_REWARDIFY_UPRIV_BADGE_LEVEL_EMAILS', 'Badge & level emails')}>
            <Toggle on={value('badgeEmails')} onClick={() => toggle('badgeEmails')} />
          </Field>
          <Field label={t('COM_REWARDIFY_UPRIV_EXPIRY_REMINDERS', 'Points-expiring reminders')} hint={t('COM_REWARDIFY_UPRIV_EXPIRY_REMINDERS_HINT', 'A heads-up before any points expire.')}>
            <Toggle on={value('expiryReminders')} onClick={() => toggle('expiryReminders')} />
          </Field>
          <Field label={t('COM_REWARDIFY_UPRIV_CAMPAIGN_UPDATES', 'Campaign updates')}>
            <Toggle on={value('campaignUpdates')} onClick={() => toggle('campaignUpdates')} />
          </Field>
        </section>

        <section className="rw-card">
          <h3 className="rw-card-t">{t('COM_REWARDIFY_UPRIV_YOUR_DATA', 'Your data')}</h3>
          <p className="rw-card-s" style={{ marginBottom: 14 }}>
            {t('COM_REWARDIFY_UPRIV_YOUR_DATA_DESC', 'Export everything Rewardify holds about you, or request removal.')}
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="rw-btn rw-btn-ghost" disabled={dataBusy !== null} onClick={() => void downloadData()}>
              {t('COM_REWARDIFY_UPRIV_DOWNLOAD_DATA', 'Download my data')}
            </button>
            <button type="button" className="rw-btn rw-btn-danger" disabled={dataBusy !== null} onClick={() => void requestRemoval()}>
              {t('COM_REWARDIFY_UPRIV_REQUEST_REMOVAL', 'Request removal')}
            </button>
          </div>
        </section>

        <section className="rw-card">
          <h3 className="rw-card-t">{t('COM_REWARDIFY_UPRIV_EXPIRY_POLICY', 'Point expiration policy')}</h3>
          <p className="rw-card-s" style={{ lineHeight: 1.6 }}>
            {t(
              'COM_REWARDIFY_UPRIV_EXPIRY_POLICY_DESC',
              "Community Points expire 365 days after they're earned, oldest first. You'll always see the exact expiry date next to each batch, and we'll remind you 14 days ahead. Reputation never expires.",
            )}
          </p>
        </section>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" className="rw-btn rw-btn-primary" onClick={onSave}>
          {t('COM_REWARDIFY_UPRIV_SAVE', 'Save preferences')}
        </button>
      </div>
    </div>
  );
}
